import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ActivityStreamItemViewModel, CONFIG_TOKEN, Patient } from '@pxl/models/common';
import { ActivityStreamService, LoginService } from '@pxl/models/contracts';
import { AppRoutingRouteNames, DashboardRoutingRouteNames, UserDashboardRoutingRouteNames } from '@pxl/route-names';
import {
    ActivityStreamDataModel,
    ActivityStreamItemModel,
    ActivityStreamOverviewModel,
    ActivityStreamService as ActivityStreamBackendService,
} from '@pxl/shared/generated';
import { LanguageService } from '@pxl/shared/translation';
import { getFormattedShortDate, getFormattedShortTime } from '@pxl/shared/util';
import { firstValueFrom } from 'rxjs';

@Injectable()
export class DefaultActivityStreamService implements ActivityStreamService {
    private readonly activityStreamBackendService = inject(ActivityStreamBackendService);
    private readonly loginService = inject(LoginService);
    private readonly languageService = inject(LanguageService);
    private readonly router = inject(Router);
    private readonly config = inject(CONFIG_TOKEN);

    public getSpecificItems(ids: string[]): Promise<ActivityStreamDataModel> {
        return firstValueFrom(
            this.activityStreamBackendService.getSpecificDataItems(ids, this.config.API_VERSION, this.loginService.getToken()),
        );
    }

    public getActivities(patientId?: string, lastChronologicalId?: number, groupId?: string): Promise<ActivityStreamDataModel> {
        return firstValueFrom(
            this.activityStreamBackendService.getDataItems(
                this.config.API_VERSION,
                this.loginService.getToken(),
                lastChronologicalId,
                patientId,
                groupId,
            ),
        );
    }

    public getActivityStreamOverview(): Promise<ActivityStreamOverviewModel> {
        return firstValueFrom(this.activityStreamBackendService.getOverview(this.config.API_VERSION, this.loginService.getToken()));
    }

    public mapToActivityStreamItemViewModelWithPatientOptional(
        activityStreamModel: ActivityStreamDataModel,
        activityStreamItem: ActivityStreamItemModel,
    ): ActivityStreamItemViewModel {
        let patient: Patient = null;

        if (activityStreamItem.patientId) {
            patient = new Patient(activityStreamModel.referencedPatients[activityStreamItem.patientId]);
        }

        return this.mapToActivityStreamItemViewModel(patient, activityStreamItem);
    }

    public mapToActivityStreamItemViewModel(patient: Patient, activityStreamItem: ActivityStreamItemModel): ActivityStreamItemViewModel {
        const formattedDateTime = this.languageService.translate('petsxl.ui.dashboard.user-dashboard.timeline.formatted-date-time', [
            {
                name: 'DATE',
                text: getFormattedShortDate(activityStreamItem.createdUtc),
            },
            {
                name: 'TIME',
                text: getFormattedShortTime(activityStreamItem.createdUtc),
            },
        ]);

        return new ActivityStreamItemViewModel(activityStreamItem, formattedDateTime, patient);
    }

    public showActivityStreamItemDetails(activityStreamItemIds: string[]): Promise<boolean> {
        return this.router.navigate([
            AppRoutingRouteNames.Dashboard,
            DashboardRoutingRouteNames.UserDashboard,
            UserDashboardRoutingRouteNames.TimelineDetail,
            activityStreamItemIds.join(','),
        ]);
    }
}
